import { Input } from "component/inputs/Input";
import React, { useCallback, useMemo, useRef } from "react";
import { Field, Form, FormSpy } from "react-final-form";
import "./EnrolledDropdown.scss";
import { EnrolledDropdownProps } from "./types";
import { BasicIcon } from "@pickit/pickit-components";
import { VALIDATION_REGEX } from "utils/constants";
import useValidationSchema from "hooks/useValidationSchema";
import classNames from "classnames";
import { SlotsConstants } from "constants/slots";
import "../i18n";
import { useTranslation } from "react-i18next";
import { FormApi } from "final-form";

export const EnrolledDropdown: React.FC<EnrolledDropdownProps> = ({
  users,
  newPicker,
  validationSchema,
  newPickerDirty,
  slot,
  initialValues,
  testId,
  addNewPicker,
  deletePicker,
  validatePicker,
  resetNewPicker,
  setPickerDirty,
}): JSX.Element => {
  const { t } = useTranslation();
  const validate = useValidationSchema(validationSchema);
  const lastPickerId = useRef("");
  const fullname = useMemo(
    () => (newPicker ? newPicker?.name + " " + newPicker.surname : "-"),
    [newPicker]
  );
  const showNewPickerInput = useMemo(() => {
    if (!slot) return false;
    const { status, taken, connected } = slot;
    const occupation = taken + connected;
    return (
      SlotsConstants.isCancellableSlot(status?.tag || "") &&
      occupation < SlotsConstants.SLOT_MAX_CAPACITY
    );
  }, [slot]);

  const renderInput = useCallback(
    (name: string, index: number, value?: string | number) => {
      return (
        <Field
          type="text"
          name={name}
          label={t("slotDetail:" + name + "Label")}
          className="Admin-Pickers-input"
          disabled
          testId={`${testId}-picker-${index}-${name}`}
          id={`${testId}-picker-${index}-${name}`}
        >
          {(props) => <Input {...props} value={value} />}
        </Field>
      );
    },
    [t, testId]
  );

  const handleBlur = useCallback(
    (id: string, form: FormApi<object, object>) => {
      if (id !== lastPickerId.current) {
        lastPickerId.current = id;
        validatePicker(id, form);
      }
    },
    [validatePicker]
  );

  const renderEnrolledPickers = useCallback(() => {
    return users?.map((user, index) => (
      <div key={user.id} className="container-enroled-colapsable-form-row">
        <div className="container-enroled-colapsable-form-col-sm-1">
          {renderInput("id", index + 1, user.id?.toString())}
        </div>
        <div className="container-enroled-colapsable-form-col-sm-1">
          {renderInput("name", index + 1, user.name + " " + user.surname)}
        </div>
        <div className="container-enroled-colapsable-form-col-sm-1">
          {renderInput("phone", index + 1, user.phone || "-")}
        </div>
        {slot?.status?.tag === "CREATED" && (
          <div className="container-enroled-colapsable-form-col-sm-1">
            <div className="button-container">
              <button
                className="delete-icon"
                type="button"
                onClick={() => deletePicker(user.id)}
                data-testId={`${testId}-picker-${index + 1}-delete`}
              >
                <BasicIcon name="trash" />
              </button>
            </div>
          </div>
        )}
      </div>
    ));
  }, [users, renderInput, slot?.status?.tag, testId, deletePicker]);

  const renderNewPicker = useCallback(
    (form, values) => {
      return (
        <>
          <FormSpy
            subscription={{
              dirty: true,
              values: true,
              invalid: true,
            }}
            onChange={(pro) => {
              setPickerDirty(pro.dirty);
            }}
          />
          <div className="container-enroled-colapsable-form-row new-picker">
            <div className="container-enroled-colapsable-form-col-sm-1">
              <Field
                type="text"
                name="id"
                label={t("slotDetail:idLabel")}
                placeholder={t("slotDetail:idPlaceholder")}
                className="Admin-Pickers-input"
                testId={`${testId}-picker-0-id`}
                id={`${testId}-picker-0-id`}
              >
                {(props) => (
                  <Input
                    {...props}
                    onBlur={handleBlur(values.id, form)}
                    pattern={VALIDATION_REGEX.regArea}
                  />
                )}
              </Field>
            </div>
            <div className="container-enroled-colapsable-form-col-sm-1">
              {renderInput("name", 0, fullname)}
            </div>
            <div className="container-enroled-colapsable-form-col-sm-1">
              {renderInput("phone", 0, newPicker?.phone || "-")}
            </div>
            <div className="container-enroled-colapsable-form-col-sm-1">
              {newPickerDirty && (
                <div
                  className={classNames("button-container", {
                    active: newPickerDirty,
                  })}
                >
                  <button
                    className="delete-icon"
                    type="button"
                    onClick={() => {
                      form.reset();
                      form.resetFieldState("id");
                      resetNewPicker();
                      setPickerDirty(false);
                    }}
                    data-testId={`${testId}-delete-new-picker`}
                  >
                    <BasicIcon name="trash" />
                  </button>
                  <button
                    disabled={!newPicker}
                    className="icon check"
                    type="button"
                    onClick={addNewPicker}
                    data-testId={`${testId}-add-new-picker`}
                  >
                    <BasicIcon name="check" color="#ffffff" />
                  </button>
                </div>
              )}
            </div>
          </div>
        </>
      );
    },
    [
      t,
      testId,
      renderInput,
      fullname,
      newPicker,
      newPickerDirty,
      addNewPicker,
      setPickerDirty,
      handleBlur,
      resetNewPicker,
    ]
  );

  return (
    <div>
      {showNewPickerInput && (
        <Form
          initialValues={initialValues}
          onSubmit={() => {}}
          validate={validate}
        >
          {({ handleSubmit, form, values }) => (
            <form onSubmit={handleSubmit}>{renderNewPicker(form, values)}</form>
          )}
        </Form>
      )}
      {renderEnrolledPickers()}
    </div>
  );
};
